import type { DeliveryAreaFee } from '@/lib/services/locations/locations.types'
import { Typography } from '@/components/ui/typography'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { DELIVERY_AREA_COLORS } from '../constants'

/** Another location's delivery boundary, shown read-only on this location's map. */
export type OtherLocationPolygon = DeliveryAreaFee & {
  locationId: number
  locationName: string
}

export const PolygonList = ({
  otherLocationsPolygons = [],
}: {
  otherLocationsPolygons?: OtherLocationPolygon[]
}) => {
  const orgId = useRouteOrganizationIdParam()

  // Sort other locations' polygons by ID descending for consistent ordering
  const sortedOtherPolygons = [...otherLocationsPolygons].sort((a, b) => {
    const aId = a.id || a.locationId || 0
    const bId = b.id || b.locationId || 0
    return bId - aId // Descending order
  })

  // Assign colors to other locations' polygons
  const otherPolygonsWithColors = sortedOtherPolygons.map((polygon, index) => {
    // Reserve index 0 for the current location's delivery boundary.
    const colorIndex = (1 + index) % DELIVERY_AREA_COLORS.length
    const color = DELIVERY_AREA_COLORS[colorIndex]

    return {
      ...polygon,
      displayColor: color,
      displayName: polygon.locationName
        ? `${polygon.locationName} - Delivery Area`
        : polygon.name || 'Delivery Area',
    }
  })

  if (otherPolygonsWithColors.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {otherPolygonsWithColors.map((polygon, index) => {
        const editUrl = `/${orgId}/settings/locations/${polygon.locationId}/deliveries`

        return (
          <div
            key={`other-${polygon.locationId || polygon.id || index}-${polygon.encoded_polygon?.substring(0, 10)}`}
            className="flex items-center justify-between p-3 border rounded-lg transition-all"
          >
            <div className="flex items-center gap-2 flex-1">
              <div
                className="w-4 h-4 rounded"
                style={{
                  border: `2px solid ${polygon.displayColor}`,
                  opacity: 0.6,
                }}
              />
              <Typography variant="supporting">{polygon.displayName}</Typography>
            </div>
            <Link
              to={editUrl}
              className="flex items-center gap-1 text-muted-foreground hover:text-interaction transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4" />
            </Link>
          </div>
        )
      })}
    </div>
  )
}
